<template>
  <div
    class="sli-form-page"
    :class="{ 'is-show': readonly }"
  >
    <div
      v-if="loading"
      class="text-center py-2"
    >
      <b-spinner variant="primary" />
    </div>
    <form
      v-else
      class="sli-form"
      :class="{ 'is-readonly': readonly }"
      @submit.prevent="readonly ? undefined : save(false)"
    >
      <section class="sli-sheet sli-sheet--title">
        <div class="sli-show-head">
          <div>
            <h1>{{ pageTitle }}</h1>
            <p v-if="!readonly">
              أدخل بيانات الحصر من الملف الورقي. الحفظ مرة واحدة في الأسفل.
            </p>
            <p v-else>
              {{ form.subject_name || '—' }}
              <span v-if="form.shahr_number"> — شهر {{ form.shahr_number }}</span>
            </p>
          </div>
          <div
            v-if="readonly"
            class="sli-show-actions"
          >
            <b-button
              v-if="canLink"
              variant="primary"
              @click="linkModal = true"
            >
              ربط الاستيلاء
            </b-button>
            <b-button
              v-if="hasPermission('edit seizure_land_inventories') && editId"
              variant="outline-primary"
              :to="{ name: 'seizure-land-inventories-edit', params: { id: editId }, query: listQuery }"
            >
              تعديل
            </b-button>
            <b-button
              variant="outline-secondary"
              :to="{ name: 'seizure-land-inventories-list', query: listQuery }"
            >
              رجوع للقائمة
            </b-button>
          </div>
        </div>
      </section>

      <sli-duplicate-warning
        v-if="!readonly"
        :duplicate="duplicate"
        @open-existing="goToRecord"
        @save-update="save(true)"
      />

      <b-alert
        v-if="identityError"
        show
        variant="danger"
        class="sli-alert"
      >
        {{ identityError }}
      </b-alert>

      <section class="sli-sheet sli-sheet--shahr">
        <header class="sli-sheet__head sli-sheet__head--row">
          <span>بيانات الشهر والخاضع والمستند</span>
          <sli-doc-icon
            :files="files"
            :readonly="readonly"
            label="المستند الدال على الشهر"
            @add="addFiles"
            @remove="removeFile"
          />
        </header>
        <div class="sli-grid sli-grid--shahr">
          <sli-field
            v-model="form.shahr_number"
            label="رقم الشهر"
            input-id="shahr-number"
            inputmode="numeric"
            compact
            size="6"
            :disabled="readonly"
            @blur="checkDuplicate"
          />
          <sli-year-combo
            v-model="form.shahr_date"
            label="تاريخ الشهر"
            input-id="shahr-date"
            :disabled="readonly"
          />
          <sli-field
            v-model="form.directorate"
            label="المديرية"
            input-id="directorate"
            fit="8"
            size="8"
            :disabled="readonly"
          />
          <div class="sli-cell sli-cell--zone">
            <label
              class="sli-cell__label"
              for="zone"
            >المنطقة</label>
            <v-select
              id="zone"
              v-model="form.zone"
              :options="zoneOptions"
              :dir="'rtl'"
              :clearable="!readonly"
              :filterable="true"
              :taggable="!readonly"
              :disabled="readonly"
              placeholder="منطقة"
              class="sli-mini-select"
            />
          </div>
          <sli-field
            v-model="form.subject_name"
            label="اسم الخاضع"
            input-id="subject-name"
            maxlength="30"
            fit="12"
            size="12"
            required
            :disabled="readonly"
            :error="subjectError"
          />
          <sli-field
            v-model="form.applicable_law"
            label="القانون الخاضع له"
            input-id="applicable-law"
            maxlength="9"
            fit="9"
            size="9"
            :disabled="readonly"
          />
          <sli-field
            v-model="form.law_year"
            label="سنة القانون"
            input-id="law-year"
            inputmode="numeric"
            maxlength="4"
            compact
            size="4"
            :disabled="readonly"
          />
          <sli-field
            v-model="form.land_code"
            label="كود الأرض"
            input-id="land-code"
            compact
            size="6"
            :disabled="readonly"
            @blur="checkDuplicate"
          />
        </div>
      </section>

      <section class="sli-sheet sli-sheet--lands">
        <header class="sli-sheet__head sli-sheet__head--row">
          <span>القطع / الأراضي ({{ form.project_locations.length }})</span>
          <div
            v-if="activeLand"
            class="sli-file-legend"
          >
            <span
              v-if="form.project_locations.length > 1"
              class="sli-file-legend__for"
            >قطعة {{ activeLandIndex + 1 }}</span>
            <sli-icon-file
              :key="`kml-${activeLand._key || activeLandIndex}`"
              :file.sync="activeLand.kmlFile"
              :existing.sync="activeLand.kmlExisting"
              :input-id="`land-kml-${activeLandIndex}`"
              variant="legend"
              label="ملف KML"
              :icon-src="kmlIcon"
              accept=".kml,.kmz,application/vnd.google-earth.kml+xml"
              :validator="fileIsAllowedKml"
              :readonly="readonly"
              :can-preview="locationHasKml(activeLand)"
              @preview="openKmlPreview(activeLand, activeLandIndex)"
            />
          </div>
        </header>

        <div
          v-for="(land, index) in form.project_locations"
          :key="land._key || land.id || index"
          class="sli-location"
          :class="[
            index % 2 === 0 ? 'is-navy' : 'is-lime',
            { 'is-active': index === activeLandIndex },
          ]"
          @focusin="activeLandIndex = index"
          @click="activeLandIndex = index"
        >
          <div
            v-if="form.project_locations.length > 1"
            class="sli-location__status"
          >
            <span
              class="sli-dot sli-dot--kml"
              :class="{ 'is-on': locationHasKml(land) }"
              title="ملف KML"
            />
          </div>
          <div class="sli-grid sli-grid--fit">
            <sli-admin-fields
              :land="land"
              :uid="`loc-${index}`"
              :zone-options="zoneOptions"
              :disabled="readonly"
              @identity-change="checkDuplicate"
            />
            <sli-field
              v-model="land.basin_name"
              label="اسم الحوض"
              :input-id="`land-basin-${index}`"
              fit="14"
              size="14"
              :disabled="readonly"
            />
            <sli-field
              v-model="land.block_number"
              label="رقم الحوض"
              :input-id="`land-block-${index}`"
              inputmode="numeric"
              compact
              size="6"
              :disabled="readonly"
            />
            <sli-field
              v-model="land.land_number"
              label="رقم القطعة"
              :input-id="`land-number-${index}`"
              inputmode="numeric"
              required
              compact
              size="6"
              :disabled="readonly"
              @blur="checkDuplicate"
            />
            <sli-fqs-inputs
              :uid="`land-fqs-${index}`"
              label="المساحة (ف/ط/س)"
              :feddan.sync="land.area_feddan"
              :qirat.sync="land.area_qirat"
              :sahm.sync="land.area_sahm"
              :disabled="readonly"
            />
            <div class="sli-cell sli-cell--tenure">
              <span class="sli-cell__label">مشاع أو مفرز(سجل عيني)</span>
              <div class="sli-radios">
                <label>
                  <input
                    :checked="isMushaa(land.tenure_type)"
                    type="radio"
                    :name="`tenure-${index}`"
                    :value="TENURE_MUSHAA"
                    :disabled="readonly"
                    @change="setTenure(land, TENURE_MUSHAA)"
                  >
                  مشاع
                </label>
                <label>
                  <input
                    :checked="isMufraz(land.tenure_type)"
                    type="radio"
                    :name="`tenure-${index}`"
                    :value="TENURE_MUFRAZ"
                    :disabled="readonly"
                    @change="setTenure(land, TENURE_MUFRAZ)"
                  >
                  مفرز(سجل عيني)
                </label>
              </div>
            </div>
            <sli-fqs-inputs
              v-if="isMushaa(land.tenure_type)"
              :uid="`land-mushaa-${index}`"
              label="من إجمالي كم (ف/ط/س) — اختياري"
              :feddan.sync="land.mushaa_feddan"
              :qirat.sync="land.mushaa_qirat"
              :sahm.sync="land.mushaa_sahm"
              :disabled="readonly"
            />
            <sli-field
              v-if="isMufraz(land.tenure_type)"
              v-model="land.new_land_number"
              label="رقم القطعة حديث"
              :input-id="`land-new-number-${index}`"
              inputmode="numeric"
              compact
              size="8"
              :disabled="readonly"
            />
            <div class="sli-bounds-grid">
              <sli-field
                v-model="land.northern_limit"
                label="بحري"
                :input-id="`land-bahari-${index}`"
                fit="10"
                size="10"
                :disabled="readonly"
              />
              <sli-field
                v-model="land.eastern_limit"
                label="شرقي"
                :input-id="`land-sharqi-${index}`"
                fit="10"
                size="10"
                :disabled="readonly"
              />
              <sli-field
                v-model="land.southern_limit"
                label="قبلي"
                :input-id="`land-qibli-${index}`"
                fit="10"
                size="10"
                :disabled="readonly"
              />
              <sli-field
                v-model="land.western_limit"
                label="غربي"
                :input-id="`land-gharbi-${index}`"
                fit="10"
                size="10"
                :disabled="readonly"
              />
            </div>
            <sli-customer-select
              :land="land"
              :input-id="`land-customer-${index}`"
              :disabled="readonly"
            />
            <sli-field
              v-model="land.taxpayer_number"
              label="رقم التكليف"
              :input-id="`land-taxno-${index}`"
              inputmode="numeric"
              compact
              size="8"
              :disabled="readonly"
            />
            <sli-field
              v-model="land.initial_seizure_report_date"
              label="تاريخ محضر الاستيلاء الابتدائي"
              :input-id="`land-seizure-${index}`"
              type="date"
              :disabled="readonly"
            />
            <sli-field
              v-model="land.notes"
              label="ملاحظات"
              :input-id="`land-notes-${index}`"
              span="6"
              multiline
              rows="2"
              :disabled="readonly"
            />
          </div>
          <sli-encroach-fields
            :land="land"
            :uid="`enc-${index}`"
            :enabled="locationHasKml(land)"
            :readonly="readonly"
            :creating-key="creatingKey"
            :can-create-project="canCreateEncProject"
            @identity-change="checkDuplicate"
            @add-encroacher="addEncroacher(land)"
            @remove-encroacher="removeEncroacher(land, $event)"
            @create-project="createEncProject($event.enc, $event.serviceId)"
          />
        </div>

        <sli-polygons-map
          :locations="form.project_locations"
          title="خريطة القطع"
        />

        <div
          v-if="!readonly"
          class="sli-add-wrap"
        >
          <button
            type="button"
            class="sli-add-btn sli-add-btn--block"
            @click="addLocation"
          >
            + إضافة قطعة
          </button>
          <button
            v-if="form.project_locations.length > 1"
            type="button"
            class="sli-link-btn sli-add-wrap__delete"
            @click="removeLocation(form.project_locations.length - 1)"
          >
            حذف
          </button>
        </div>
      </section>

      <sli-team-fields
        v-model="form.team_members"
        :error="teamError"
        :disabled="readonly"
      />

      <div
        v-if="!readonly"
        class="sli-submit"
      >
        <button
          type="submit"
          class="sli-save"
          :disabled="saving"
        >
          <b-spinner
            v-if="saving"
            small
          />
          <span v-else>حفظ</span>
        </button>
      </div>
    </form>

    <sli-kml-map-modal
      :visible.sync="kmlPreview.visible"
      :file="kmlPreview.file"
      :url="kmlPreview.url"
      :coordinates="kmlPreview.coordinates"
      :title="kmlPreview.title"
    />

    <sli-link-seizure-modal
      :visible.sync="linkModal"
      :inventory-id="editId"
      @linked="restoreOrLoad"
    />
  </div>
</template>

<script>
import { BAlert, BButton, BSpinner } from 'bootstrap-vue'
import vSelect from 'vue-select'
import seizureLandInventoriesApi from '@/api/seizure-land-inventories/seizureLandInventories'
import SliField from './components/SliField.vue'
import SliFqsInputs from './components/SliFqsInputs.vue'
import SliDocIcon from './components/SliDocIcon.vue'
import SliYearCombo from './components/SliYearCombo.vue'
import SliIconFile from './components/SliIconFile.vue'
import SliEncroachFields from './components/SliEncroachFields.vue'
import SliDuplicateWarning from './components/SliDuplicateWarning.vue'
import SliAdminFields from './components/SliAdminFields.vue'
import SliCustomerSelect from './components/SliCustomerSelect.vue'
import SliKmlMapModal from './components/SliKmlMapModal.vue'
import SliTeamFields from './components/SliTeamFields.vue'
import SliPolygonsMap from './components/SliPolygonsMap.vue'
import SliLinkSeizureModal from './components/SliLinkSeizureModal.vue'
import {
  createEmptyForm,
  emptyLand,
  emptyEncroacher,
  emptyTeamMember,
  copyAdminFrom,
  landHasData,
  locationHasKml,
  inventoryToForm,
  inventoryFromResponse,
  toYearValue,
  existingAttachments,
  saveDraft,
  loadDraft,
  clearDraft,
  buildFormData,
  buildPayload,
  hasMultipartUploads,
  canSaveIdentity,
  shouldCheckDuplicate,
  duplicateCheckBody,
  parseDuplicateResponse,
  numericInventoryId,
  fileIsAllowed,
  fileIsAllowedKml,
  normalizeLand,
  hydrateLandEncroachment,
  canLinkSeizure,
  createProjectBody,
  SERVICE_AGRICULTURAL,
  SERVICE_AHKAR,
  TENURE_MUSHAA,
  TENURE_MUFRAZ,
  DEFAULT_DIRECTORATE,
  DEFAULT_GOVERNORATE_ID,
  ZONE_OPTIONS,
  isMushaa,
  isMufraz,
  serializeTeamMembers,
  HEADER_AGRICULTURAL_REFORM,
  TAB_MAPS,
} from './formModel'

export default {
  name: 'SeizureLandInventoryWizard',
  components: {
    BAlert,
    BButton,
    BSpinner,
    SliField,
    SliFqsInputs,
    SliDocIcon,
    SliYearCombo,
    SliIconFile,
    SliEncroachFields,
    SliDuplicateWarning,
    SliAdminFields,
    SliCustomerSelect,
    SliKmlMapModal,
    SliTeamFields,
    SliPolygonsMap,
    SliLinkSeizureModal,
    vSelect,
  },
  props: {
    readonly: { type: Boolean, default: false },
  },
  data() {
    return {
      form: createEmptyForm(),
      files: [],
      saving: false,
      loading: false,
      ready: false,
      subjectError: '',
      identityError: '',
      duplicate: null,
      teamError: '',
      draftTimer: null,
      dupTimer: null,
      TENURE_MUSHAA,
      TENURE_MUFRAZ,
      zoneOptions: ZONE_OPTIONS.slice(),
      kmlIcon: require('@/assets/images/icons/KMLIcon.jpeg'),
      activeLandIndex: 0,
      kmlPreview: {
        visible: false,
        file: null,
        url: '',
        coordinates: [],
        title: '',
      },
      removedDocumentIds: [],
      linkModal: false,
      creatingKey: '',
    }
  },
  computed: {
    editId() {
      return this.$route.params.id || null
    },
    isEdit() {
      return !!this.editId && this.$route.name !== 'seizure-land-inventories-create'
    },
    pageTitle() {
      if (this.readonly) return 'عرض الحصر'
      return this.isEdit ? 'تعديل حصر أراضي الاستيلاء' : 'قائمة حصر وتحديد — أراضي الاستيلاء'
    },
    listQuery() {
      const tab = this.$route.query.tab || this.form.header_type
      return tab ? { tab } : {}
    },
    canLink() {
      return canLinkSeizure({
        ...this.form,
        parcels: this.form.project_locations,
      })
    },
    canCreateEncProject() {
      return this.hasPermission('create projects')
    },
    activeLand() {
      const list = this.form.project_locations || []
      if (!list.length) return null
      const index = Math.min(Math.max(this.activeLandIndex, 0), list.length - 1)
      return list[index] || null
    },
  },
  watch: {
    'form.zone'(value) {
      const name = String(value || '').trim()
      if (name && !this.zoneOptions.includes(name)) {
        this.zoneOptions.push(name)
      }
    },
    form: {
      deep: true,
      handler() {
        this.queueDraft()
        const first = (this.form.project_locations || [])[0]
        if (first && first.activity && !this.form.usage_type) {
          this.form.usage_type = first.activity
        }
      },
    },
  },
  created() {
    if (!this.isEdit && !this.readonly) {
      const canCreate = this.hasPermission('create seizure_land_inventories')
        || this.hasPermission('create projects')
      if (!canCreate) {
        this.$router.replace({ name: 'misc-not-authorized' })
        return
      }
    }
    this.restoreOrLoad()
  },
  beforeDestroy() {
    if (this.draftTimer) clearTimeout(this.draftTimer)
    if (this.dupTimer) clearTimeout(this.dupTimer)
    this.files.forEach(item => {
      if (item.preview) URL.revokeObjectURL(item.preview)
    })
  },
  methods: {
    fileIsAllowed,
    fileIsAllowedKml,
    locationHasKml,
    isMushaa,
    isMufraz,
    setTenure(land, value) {
      land.tenure_type = value
      if (isMushaa(value)) {
        land.new_land_number = ''
        land.newMapFile = null
        land.newMapExisting = null
      } else {
        land.mushaa_feddan = ''
        land.mushaa_qirat = ''
        land.mushaa_sahm = ''
        land.mushaa_total = ''
      }
    },
    openKmlPreview(land, index) {
      this.kmlPreview = {
        visible: true,
        file: land.kmlFile instanceof File ? land.kmlFile : null,
        url: land.kmlExisting && (land.kmlExisting.url || land.kmlExisting.download_url)
          ? (land.kmlExisting.url || land.kmlExisting.download_url)
          : '',
        coordinates: land.coordinates || [],
        title: `قطعة ${index + 1} — عرض KML`,
      }
    },
    applyInventory(res) {
      const inventory = inventoryFromResponse(res)
      const hydrated = inventoryToForm(inventory)
      this.form = hydrated
      this.files = existingAttachments(inventory)
      this.removedDocumentIds = []
      this.normalizeLocations()
      this.ready = true
    },
    restoreOrLoad() {
      const draft = this.readonly ? null : loadDraft(this.editId)
      if (this.isEdit) {
        this.loading = true
        seizureLandInventoriesApi.show(this.editId)
          .then(res => {
            this.applyInventory(res)
          })
          .catch(() => {
            this.$toast.error('تعذر تحميل الحصر')
            if (draft && draft.form) {
              this.form = { ...createEmptyForm(), ...draft.form }
              this.normalizeLocations()
              this.ready = true
            }
          })
          .finally(() => {
            this.loading = false
          })
        return
      }
      if (draft && draft.form) {
        this.form = { ...createEmptyForm(), ...draft.form }
        this.normalizeLocations()
        this.ready = true
        return
      }
      this.normalizeLocations()
      this.ready = true
    },
    keepMediaIds(targetLands, sourceLands) {
      (targetLands || []).forEach((land, index) => {
        const source = (sourceLands || [])[index]
        if (!land || !source) return
        if (!land.mapMediaId) land.mapMediaId = source.mapMediaId || null
        if (!land.kmlMediaId) land.kmlMediaId = source.kmlMediaId || null
        if (!land.newMapMediaId) land.newMapMediaId = source.newMapMediaId || null
      })
    },
    normalizeLocations() {
      const list = this.form.project_locations || []
      this.form.project_locations = list.length
        ? list.map(land => normalizeLand(land))
        : [emptyLand()]
      this.form.shahr_date = toYearValue(this.form.shahr_year) || toYearValue(this.form.shahr_date)
      hydrateLandEncroachment(this.form)
      this.form.create_project = false
      const queryType = this.$route.query.header_type || this.$route.query.tab
      if (!this.isEdit && queryType && queryType !== TAB_MAPS) {
        this.form.header_type = queryType
      }
      if (!this.form.header_type) this.form.header_type = HEADER_AGRICULTURAL_REFORM
      if (!Array.isArray(this.form.team_members) || !this.form.team_members.length) {
        this.form.team_members = [emptyTeamMember()]
      }
      if (!this.isEdit) {
        if (!String(this.form.directorate || '').trim()) {
          this.form.directorate = DEFAULT_DIRECTORATE
        }
        this.form.project_locations.forEach(land => {
          if (!land.governorate_id) land.governorate_id = DEFAULT_GOVERNORATE_ID
        })
      }
    },
    queueDraft() {
      if (this.readonly || !this.ready) return
      if (this.draftTimer) clearTimeout(this.draftTimer)
      this.draftTimer = setTimeout(() => saveDraft(this.editId, this.form, 0), 250)
    },
    addFiles(list) {
      this.files = this.files.concat(list)
    },
    removeFile(index) {
      const item = this.files[index]
      if (item && item.preview) URL.revokeObjectURL(item.preview)
      if (item && item.existing && item.id) {
        this.removedDocumentIds.push(item.id)
      }
      this.files.splice(index, 1)
    },
    addLocation() {
      const prev = this.form.project_locations[this.form.project_locations.length - 1]
      const next = copyAdminFrom(prev)
      if (!next.zone) next.zone = (prev && prev.zone) || this.form.zone || ''
      this.form.project_locations.push(next)
      this.activeLandIndex = this.form.project_locations.length - 1
    },
    async removeLocation(index) {
      const ok = await this.$bvModal.msgBoxConfirm('هل تريد حذف هذه القطعة؟', {
        title: 'تأكيد الحذف',
        okTitle: 'حذف',
        cancelTitle: 'إلغاء',
        okVariant: 'danger',
        centered: true,
      })
      if (ok) {
        this.form.project_locations.splice(index, 1)
        if (this.activeLandIndex >= this.form.project_locations.length) {
          this.activeLandIndex = Math.max(this.form.project_locations.length - 1, 0)
        }
      }
    },
    addEncroacher(land) {
      if (!land || !this.locationHasKml(land)) return
      if (!Array.isArray(land.encroacher_data)) this.$set(land, 'encroacher_data', [])
      land.encroacher_data.push(emptyEncroacher())
    },
    removeEncroacher(land, index) {
      if (!land || !Array.isArray(land.encroacher_data)) return
      land.encroacher_data.splice(index, 1)
    },
    checkDuplicate() {
      if (this.dupTimer) clearTimeout(this.dupTimer)
      this.dupTimer = setTimeout(() => this.runDuplicateCheck(), 400)
    },
    runDuplicateCheck() {
      if (!shouldCheckDuplicate(this.form)) return
      seizureLandInventoriesApi.checkDuplicate(duplicateCheckBody(this.form))
        .then(res => {
          const parsed = parseDuplicateResponse(res)
          if (parsed.duplicate && parsed.inventory && this.editId && String(parsed.inventory.id) === String(this.editId)) {
            this.duplicate = null
            return
          }
          this.duplicate = parsed.duplicate ? parsed : null
        })
        .catch(err => {
          const parsed = parseDuplicateResponse(err?.response?.data, err?.response?.status)
          this.duplicate = parsed.duplicate ? parsed : null
        })
    },
    goToRecord(id) {
      const numericId = numericInventoryId(id)
      if (!numericId) return
      const tab = this.form.header_type || this.$route.query.tab
      this.$router.push({
        name: 'seizure-land-inventories-show',
        params: { id: numericId },
        query: tab ? { tab } : {},
      })
    },
    save(updateIfExists) {
      if (this.readonly) return
      this.subjectError = ''
      this.identityError = ''
      this.teamError = ''
      if (!String(this.form.subject_name || '').trim()) {
        this.subjectError = 'مطلوب'
        this.$toast.error('اسم الخاضع مطلوب')
        return
      }
      const landHasDataFn = landHasData
      if ((this.form.project_locations || []).some(l => landHasDataFn(l) && !String(l.land_number || '').trim())) {
        this.$toast.error('كل قطعة مستخدمة يجب أن يكون لها رقم قطعة')
        return
      }
      if (!canSaveIdentity(this.form)) {
        this.identityError = 'للحفظ يلزم اسم الخاضع، ومعه (المحافظة + المركز + القرية/الناحية + رقم القطعة)'
        this.$toast.error(this.identityError)
        return
      }
      if (!serializeTeamMembers(this.form.team_members).length) {
        this.teamError = 'فريق العمل مطلوب — أضف موظفاً واحداً على الأقل'
        this.$toast.error(this.teamError)
        return
      }
      const leads = serializeTeamMembers(this.form.team_members).filter(row => row.is_lead)
      if (leads.length !== 1) {
        this.teamError = 'حدد قائداً واحداً للفريق'
        this.$toast.error(this.teamError)
        return
      }

      this.form.create_project = false
      this.form.update_if_exists = !!updateIfExists
      this.saving = true
      const newFiles = this.files.filter(f => !f.existing)
      const saveOptions = { removeDocumentIds: this.removedDocumentIds }
      const useMultipart = !this.isEdit || hasMultipartUploads(this.form, newFiles)
      const body = useMultipart
        ? buildFormData(this.form, newFiles, saveOptions)
        : buildPayload(this.form, saveOptions)
      const request = this.isEdit
        ? seizureLandInventoriesApi.update(this.editId, body)
        : seizureLandInventoriesApi.store(body)

      request
        .then(res => {
          const inventory = inventoryFromResponse(res)
          const id = numericInventoryId(inventory?.id) || numericInventoryId(this.editId)
          clearDraft(this.editId)
          this.$toast.success('تم حفظ الحصر بنجاح')
          const tab = this.form.header_type || this.$route.query.tab
          if (id) {
            this.$router.replace({
              name: 'seizure-land-inventories-show',
              params: { id },
              query: tab ? { tab } : {},
            })
          } else {
            this.$router.replace({
              name: 'seizure-land-inventories-list',
              query: tab ? { tab } : {},
            })
          }
        })
        .catch(err => {
          const status = err?.response?.status
          const data = err?.response?.data
          if (status === 409) {
            this.duplicate = parseDuplicateResponse(data, 409)
            this.$toast.error('يوجد سجل مطابق — راجع لوحة التحذير أعلى النموذج')
            window.scrollTo(0, 0)
            return
          }
          if (status === 422) {
            this.identityError = data?.message || 'تحقق من البيانات المطلوبة'
            this.$toast.error(this.identityError)
            return
          }
          this.$toast.error((data && data.message) || 'تعذر حفظ الحصر')
        })
        .finally(() => {
          this.saving = false
        })
    },
    createEncProject(enc, serviceId) {
      if (Number(serviceId) === 108) return
      if (!this.editId || !enc || !enc.id) {
        this.$toast.error('يلزم حفظ التعدي أولاً للحصول على معرّف')
        return
      }
      if (enc.project_id) return
      const sid = Number(serviceId) === SERVICE_AHKAR ? SERVICE_AHKAR : SERVICE_AGRICULTURAL
      const body = createProjectBody(enc.service_id || sid)
      body.service_id = sid
      this.creatingKey = `${enc.id || enc._key}-${sid}`
      seizureLandInventoriesApi.createEncroachmentProject(this.editId, enc.id, body)
        .catch(err => {
          if (err?.response?.status === 404) {
            return seizureLandInventoriesApi.createProject(this.editId, { ...body, encroachment_id: enc.id })
          }
          return Promise.reject(err)
        })
        .then(res => {
          const payload = inventoryFromResponse(res) || res
          const project = payload?.project || res?.data?.project || res?.project
          const projectId = project?.id || payload?.project_id || res?.data?.project_id
          this.$toast.success(projectId ? `تم إنشاء المشروع ${projectId}` : 'تم إنشاء مشروع التعدي')
          this.restoreOrLoad()
          if (projectId) {
            this.$router.push({ name: 'project_details', params: { id: projectId } })
          }
        })
        .catch(err => {
          const status = err?.response?.status
          const data = err?.response?.data
          if (status === 409) {
            const projectId = data?.project_id || data?.data?.project_id || data?.project?.id
            this.$toast.error(projectId ? `هذا التعدي له مشروع مسبقاً (${projectId})` : 'هذا التعدي له مشروع مسبقاً')
            this.restoreOrLoad()
            return
          }
          this.$toast.error(data?.message || 'تعذر إنشاء مشروع التعدي')
        })
        .finally(() => {
          this.creatingKey = ''
        })
    },
  },
}
</script>

<style>
.sli-form-page {
  background: #e8eef5;
  margin: -1.2rem -1.2rem 0;
  padding: 16px 12px 96px;
  min-height: calc(100vh - 80px);
  overflow-x: hidden;
}
.sli-form {
  width: 100%;
  max-width: none;
}
.sli-sheet {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(6, 40, 77, 0.12), 0 1px 3px 1px rgba(6, 40, 77, 0.08);
  margin-bottom: 12px;
  overflow: visible;
}
.sli-sheet--title {
  background: #06284D;
  border-top: 8px solid #5CC10B;
  padding: 16px 18px 14px;
  overflow: hidden;
}
.sli-show-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}
.sli-show-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex: 0 0 auto;
}
.sli-enc-project {
  flex: 1 1 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.sli-form.is-readonly .sli-cell__input:disabled,
.sli-form.is-readonly .sli-cell__input--area:disabled,
.sli-form.is-readonly .sli-mini-select .vs__dropdown-toggle {
  color: #06284D;
  opacity: 1;
  -webkit-text-fill-color: #06284D;
}
.sli-sheet--title h1 {
  font-size: 1.35rem;
  font-weight: 600;
  color: #fff;
  margin: 0 0 4px;
}
.sli-sheet--title p {
  margin: 0;
  color: rgba(255, 255, 255, 0.78);
  font-size: 0.9rem;
}
.sli-sheet--shahr {
  background: #e8f3dc;
  border-top: 6px solid #5CC10B;
  overflow: visible;
}
.sli-sheet--shahr .sli-sheet__head {
  background: rgba(92, 193, 11, 0.18);
}
.sli-grid.sli-grid--shahr,
.sli-grid.sli-grid--fit {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 0;
}
.sli-grid.sli-grid--shahr {
  flex-wrap: nowrap;
}
.sli-admin-grid,
.sli-bounds-grid {
  display: contents;
}
.sli-grid.sli-grid--shahr > .sli-cell,
.sli-grid.sli-grid--fit .sli-cell {
  flex: 0 1 auto;
  width: max-content;
  max-width: 100%;
  min-width: 0;
}
.sli-grid.sli-grid--shahr > .sli-cell--compact {
  min-width: 5.25rem;
}
.sli-grid.sli-grid--shahr > .sli-cell--year {
  min-width: 6.4rem;
}
.sli-grid.sli-grid--shahr > .sli-cell--zone,
.sli-cell--zone {
  min-width: 7.25rem;
}
.sli-cell--gov {
  min-width: 8.25rem;
}
.sli-cell--center {
  min-width: 7.5rem;
}
.sli-cell--village {
  min-width: 8.75rem;
}
.sli-cell--tenure {
  min-width: 11rem;
}
.sli-cell--customer {
  min-width: 11rem;
}
.sli-cell--date {
  min-width: 9.75rem;
}
.sli-span-6,
.sli-cell--full {
  flex: 1 1 100%;
  width: 100%;
}
.sli-sheet--lands {
  background: #f4f7fb;
  border-top: 6px solid #06284D;
}
.sli-sheet__head {
  padding: 8px 12px;
  font-weight: 600;
  font-size: 0.95rem;
  color: #06284D;
  border-bottom: 1px solid rgba(6, 40, 77, 0.1);
  position: relative;
}
.sli-sheet__head small {
  display: block;
  font-weight: 400;
  color: #5f6368;
  font-size: 0.75rem;
}
.sli-sheet__head--row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}
.sli-file-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
}
.sli-file-legend__for {
  font-size: 11px;
  font-weight: 700;
  color: #5CC10B;
  margin-left: 4px;
}
.sli-icon-file--legend {
  flex-direction: row;
  align-items: center;
  min-width: 0;
  gap: 4px;
}
.sli-icon-file--legend .sli-icon-file__hit {
  width: 32px;
  height: 32px;
}
.sli-icon-file--legend .sli-icon-file__label {
  max-width: none;
  font-size: 11px;
  cursor: pointer;
}
.sli-icon-file--legend .sli-icon-file__actions {
  margin-right: 2px;
}
.sli-cell {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  box-sizing: border-box;
  width: max-content;
  max-width: 100%;
  min-width: 0;
  padding: 8px 12px 10px 14px;
  border-top: 1px solid rgba(6, 40, 77, 0.1);
  border-left: 1px solid rgba(6, 40, 77, 0.1);
  margin: 0;
}
.sli-span-1,
.sli-span-2,
.sli-span-3,
.sli-span-4 { grid-column: auto; }
.sli-fqs {
  width: 100%;
  max-width: 13.5rem;
}
.sli-cell__label {
  display: block;
  font-size: 11px;
  line-height: 1.25;
  color: #06284D;
  margin: 0 0 4px;
  font-weight: 600;
  white-space: nowrap;
}
.sli-req { color: #d93025; margin-right: 2px; }
.sli-cell__input,
.sli-mini-select .vs__dropdown-toggle {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;
  border: 0;
  border-bottom: 1px solid #c5ced8;
  border-radius: 0;
  background: transparent;
  height: 32px;
  padding: 2px 0;
  font-size: 0.95rem;
  color: #06284D;
  box-shadow: none !important;
}
.sli-mini-select {
  width: 100%;
  min-width: 0;
}
.sli-span-6,
.sli-cell--full {
  flex: 1 1 100%;
  width: 100%;
  max-width: 100%;
}
.sli-cell__input--area {
  width: 100%;
  height: auto;
  min-height: 48px;
  resize: vertical;
}
.sli-cell__input:focus,
.sli-mini-select .vs__dropdown-toggle:focus-within {
  border-bottom-color: #5CC10B;
  outline: 0;
}
.sli-cell__err {
  display: block;
  color: #d93025;
  font-size: 11px;
}
.sli-mini-select {
  font-size: 0.9rem;
}
.sli-mini-select .vs__dropdown-toggle {
  min-height: 32px;
  height: 32px;
  padding: 0;
}
.sli-mini-select .vs__search,
.sli-mini-select .vs__selected {
  margin: 0;
  padding: 0 4px;
  font-size: 0.9rem;
}
.sli-mini-select .vs__dropdown-menu {
  max-height: 240px;
}
.sli-year {
  position: relative;
  display: flex;
  align-items: stretch;
  min-width: 5.5rem;
}
.sli-year__input {
  width: 4.6ch !important;
  max-width: 4.6ch;
  flex: 0 0 auto;
}
.sli-year__toggle {
  border: 0;
  background: transparent;
  color: #06284D;
  padding: 0 2px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
}
.sli-year__list {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 20;
  width: 6.5rem;
  max-height: 220px;
  overflow: auto;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  background: #fff;
  border: 1px solid #d5dde6;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(6, 40, 77, 0.16);
}
.sli-year__list li {
  padding: 4px 10px;
  cursor: pointer;
  font-size: 0.9rem;
}
.sli-year__list li:hover,
.sli-year__list li.is-active {
  background: #e8f3dc;
  color: #06284D;
}
.sli-doc-icon {
  position: relative;
}
.sli-doc-icon__btn {
  position: relative;
  width: 36px;
  height: 36px;
  border: 1px solid rgba(6, 40, 77, 0.18);
  border-radius: 8px;
  background: #fff;
  color: #06284D;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.sli-doc-icon__btn.is-on {
  border-color: #5CC10B;
  color: #5CC10B;
}
.sli-doc-icon__badge {
  position: absolute;
  top: -6px;
  left: -6px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: #5CC10B;
  color: #fff;
  font-size: 10px;
  line-height: 16px;
  font-weight: 700;
}
.sli-doc-icon__panel {
  position: absolute;
  top: calc(100% + 6px);
  left: auto;
  right: 0;
  z-index: 30;
  width: min(22rem, calc(100vw - 32px));
  background: #fff;
  border: 1px solid #d5dde6;
  border-radius: 8px;
  box-shadow: 0 10px 28px rgba(6, 40, 77, 0.18);
  padding: 10px;
}
.sli-doc-icon__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  color: #06284D;
}
.sli-doc-icon__drop {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px dashed #9eb0c3;
  border-radius: 6px;
  padding: 8px 10px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: #06284D;
  margin: 0;
}
.sli-doc-icon__input {
  display: none;
}
.sli-doc-icon__hint {
  display: block;
  margin: 6px 0 8px;
  color: #5f6368;
  font-size: 11px;
}
.sli-doc-icon__list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.sli-doc-icon__row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.sli-doc-icon__preview {
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  border-radius: 4px;
  overflow: hidden;
  background: #f3f3f3;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  color: #c0392b;
}
.sli-doc-icon__preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.sli-doc-icon__meta {
  flex: 1;
  min-width: 0;
}
.sli-doc-icon__name {
  display: block;
  font-size: 12px;
  font-weight: 600;
  word-break: break-word;
}
.sli-location {
  border-top: 8px solid #e8eef5;
}
.sli-location.is-navy {
  background: #e8eef5;
  border-right: 4px solid #06284D;
}
.sli-location.is-lime {
  background: #eef6e4;
  border-right: 4px solid #5CC10B;
}
.sli-location.is-active {
  outline: 2px solid #5CC10B;
  outline-offset: -2px;
}
.sli-location__status {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px 0;
}
.sli-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(6, 40, 77, 0.2);
}
.sli-dot.is-on {
  background: #5CC10B;
}
.sli-location__files {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 10px 14px;
  padding: 8px 10px 4px;
}
.sli-icon-file {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  min-width: 4.5rem;
  position: relative;
}
.sli-icon-file__input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.sli-icon-file__hit {
  width: 40px;
  height: 40px;
  border: 1px solid rgba(6, 40, 77, 0.2);
  border-radius: 8px;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #06284D;
  margin: 0;
  position: relative;
}
.sli-icon-file.is-on .sli-icon-file__hit {
  border-color: #5CC10B;
}
.sli-icon-file.is-disabled {
  opacity: 0.4;
  pointer-events: none;
}
.sli-icon-file__img {
  width: 22px;
  height: 22px;
  object-fit: contain;
}
.sli-icon-file__tick {
  display: none;
  position: absolute;
  top: -4px;
  left: -4px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #5CC10B;
}
.sli-icon-file.is-on .sli-icon-file__tick {
  display: block;
}
.sli-icon-file__label {
  font-size: 10px;
  font-weight: 700;
  color: #06284D;
  text-align: center;
  line-height: 1.2;
  max-width: 6.5rem;
}
.sli-icon-file__actions {
  display: flex;
  gap: 6px;
}
.sli-encroach {
  background: rgba(92, 193, 11, 0.08);
  border-top: 1px solid rgba(6, 40, 77, 0.1);
}
.sli-encroach.is-locked {
  opacity: 0.72;
}
.sli-encroach__head {
  padding: 8px 12px 4px;
  font-weight: 700;
  font-size: 0.85rem;
  color: #06284D;
}
.sli-encroach__head small {
  display: block;
  font-weight: 400;
  color: #d93025;
  font-size: 0.72rem;
}
.sli-add-wrap {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 12px 12px;
  border-top: 1px solid rgba(6, 40, 77, 0.1);
}
.sli-add-btn {
  background: transparent;
  border: 0;
  color: #06284D;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 4px 0;
  cursor: pointer;
}
.sli-add-btn:hover {
  color: #5CC10B;
}
.sli-add-btn--block {
  text-align: right;
}
.sli-add-wrap__delete {
  min-height: auto;
}
.sli-link-btn {
  background: none;
  border: 0;
  color: #d93025;
  font-size: 0.8rem;
  cursor: pointer;
  padding: 0;
}
.sli-map-btn {
  background: none;
  border: 0;
  color: #06284D;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
}
.sli-map-btn:hover {
  color: #5CC10B;
}
.sli-enc-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px 4px;
  font-weight: 600;
  font-size: 0.85rem;
  color: #06284D;
  border-top: 1px solid rgba(6, 40, 77, 0.1);
}
.sli-enc-row {
  border-top: 4px solid #e8f4d8;
}
.sli-cell--action {
  display: flex;
  align-items: flex-end;
  padding-bottom: 10px;
}
.sli-radios {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  min-height: 32px;
  font-size: 0.85rem;
}
.sli-radios label {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  font-weight: 600;
  cursor: pointer;
  color: #06284D;
  white-space: nowrap;
}
.sli-lock {
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
}
.sli-alert {
  width: 100%;
  max-width: none;
  margin: 0 0 12px;
}
.sli-submit {
  position: sticky;
  bottom: 0;
  z-index: 8;
  background: #e8eef5;
  padding: 10px 0 calc(8px + env(safe-area-inset-bottom, 0px));
  display: flex;
  justify-content: flex-start;
}
.sli-save {
  background: #06284D;
  color: #fff;
  border: 0;
  border-radius: 4px;
  min-width: 120px;
  height: 40px;
  font-weight: 600;
  cursor: pointer;
}
.sli-save:hover:not(:disabled) {
  background: #5CC10B;
}
.sli-save:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
@media (max-width: 1199.98px) {
  .sli-form-page {
    padding: 12px 10px 112px;
  }
  .sli-grid.sli-grid--shahr {
    flex-wrap: wrap;
  }
  .sli-cell {
    padding: 8px 12px 10px 14px;
  }
  .sli-cell__label {
    font-size: 12px;
  }
  .sli-cell__input,
  .sli-mini-select .vs__dropdown-toggle {
    height: 42px;
    min-height: 42px;
    font-size: 16px;
  }
  .sli-cell__input--area {
    min-height: 64px;
  }
  .sli-fqs {
    max-width: 16rem;
  }
  .sli-radios {
    min-height: 42px;
  }
  .sli-radios label {
    min-height: 42px;
    padding: 6px 8px;
  }
  .sli-add-btn,
  .sli-map-btn,
  .sli-link-btn,
  .sli-doc-icon__btn,
  .sli-icon-file__hit {
    min-height: 44px;
  }
  .sli-icon-file__hit {
    width: 44px;
    height: 44px;
  }
  .sli-save {
    min-width: 168px;
    height: 48px;
    font-size: 1.05rem;
  }
  .sli-sheet--title h1 {
    font-size: 1.2rem;
  }
}

@media (max-width: 991.98px) {
  .sli-show-head {
    flex-direction: column;
    align-items: stretch;
  }
  .sli-show-actions .btn {
    flex: 1 1 auto;
  }
  .sli-sheet__head--row,
  .sli-enc-head,
  .sli-add-wrap,
  .sli-location__files {
    gap: 8px;
    flex-wrap: wrap;
  }
  .sli-save {
    width: 100%;
    max-width: 320px;
  }
}

@media (max-width: 575.98px) {
  .sli-form-page {
    margin: -1rem -1rem 0;
  }
  .sli-grid.sli-grid--shahr > .sli-cell,
  .sli-grid.sli-grid--fit .sli-cell {
    flex: 1 1 calc(50% - 1px);
    width: auto;
    min-width: 0;
    max-width: 50%;
  }
  .sli-span-6,
  .sli-cell--full,
  .sli-cell--tenure {
    flex: 1 1 100%;
    width: 100%;
    max-width: 100%;
  }
  .sli-cell__label {
    white-space: normal;
  }
  .sli-fqs {
    max-width: none;
  }
  .sli-year__list {
    right: auto;
    left: 0;
  }
}

@media (pointer: coarse) {
  .sli-cell__input,
  .sli-mini-select .vs__dropdown-toggle {
    min-height: 44px;
    height: 44px;
    font-size: 16px;
  }
  .sli-radios label {
    min-height: 44px;
    padding: 8px 10px;
  }
}
</style>
